import { Icon, Icons } from '@/components/icon';
import { INTEGRATION_STYLES } from '@/components/integrations/integrations';
import { getTagColors } from '@/components/ui/color-picker';
import { Avatar, Badge, Chip } from '@/components/ui/design-system';
import { P3 } from '@/components/ui/text';
import { useResolvedMediaUrl } from '@/hooks/use-resolved-media-url';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import type { components } from '@/services/api-allo/api-allo-v1';
import { memo } from 'react';

type ContactEvent = components['schemas']['ContactEvent'];
type CompanyEvent = components['schemas']['CompanyEventV2Response'];
type ActivityEvent = ContactEvent | CompanyEvent;

type ActivityDisplay = {
  icon: Icons;
  textKey: string;
  iconClassName?: string;
  interpolation?: Record<string, string>;
  /** Integration info for activities from integrations */
  integration?: {
    type: string;
    name: string;
    logo?: string;
  };
  /** User who performed the action (rendered as [prefixKey] [avatar] [name] [suffixKey]) */
  user?: {
    name: string;
    imageUrl?: string | null;
    /** Text before the avatar+name (e.g. "Called by") */
    prefixKey: string;
    /** Optional text after the name (e.g. ", no one answered") */
    suffixKey?: string;
  };
  /** Tag info for call tag events */
  tag?: {
    name: string;
    colors: { text: string; background: string };
  };
};

function isCompanyEvent(activity: ActivityEvent): activity is CompanyEvent {
  return 'user_account' in activity;
}

/** Map integration_type from API to integration name for display (Capital Case) */
function getIntegrationDisplayName(integrationType: string): string {
  // Normalize the integration type (e.g., "apollo-oauth" -> "Apollo", "ATTIO" -> "Attio")
  const normalized = integrationType
    .replace(/-oauth$/, '')
    .replace(/_/g, ' ')
    .toLowerCase();
  // Capitalize first letter of each word (Capital Case)
  return normalized
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getActivityDisplay(activity: ActivityEvent): ActivityDisplay {
  const payload = activity.payload as {
    status?: string;
    call_status?: string;
    details?: string;
    call_detail?: string;
    contact_name?: string;
    contact_last_name?: string;
    integration_type?: string;
    // Call tag event fields
    tag_name?: string;
    tag_key?: string;
    tag_hex_color?: string | null;
    source?: 'AI' | 'MANUAL';
  };
  const callStatus = payload?.call_status ?? payload?.status;
  const callDetail = payload?.call_detail ?? payload?.details;
  const isAnswered = callStatus === 'completed';
  const userName = isCompanyEvent(activity) ? activity.user_account?.name : activity.user?.name;
  const userImageUrl = !isCompanyEvent(activity) ? activity.user?.image_url : undefined;
  const contactName = [payload?.contact_name, payload?.contact_last_name].filter(Boolean).join(' ');
  const userDisplay = userName ? { name: userName, imageUrl: userImageUrl } : undefined;

  switch (activity.type) {
    case 'CONTACT_CREATED':
      return {
        icon: Icons.Plus,
        textKey: 'contacts.activity.contactCreated',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.createdBy' }
          : undefined,
      };
    case 'CONTACT_FIELD_UPDATED':
      return {
        icon: Icons.Plus,
        textKey: 'contacts.activity.contactUpdated',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.updatedBy' }
          : undefined,
      };
    case 'CONTACT_NUMBER_ADDED':
      return {
        icon: Icons.Plus,
        textKey: 'contacts.activity.phoneNumberAdded',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.phoneAddedBy' }
          : undefined,
      };
    case 'CONTACT_NUMBER_REMOVED':
      return {
        icon: Icons.Minus,
        textKey: 'contacts.activity.phoneNumberRemoved',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.phoneRemovedBy' }
          : undefined,
      };
    case 'CONTACT_EMAIL_ADDED':
      return {
        icon: Icons.Plus,
        textKey: 'contacts.activity.emailAdded',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.emailAddedBy' }
          : undefined,
      };
    case 'CONTACT_EMAIL_REMOVED':
      return {
        icon: Icons.Minus,
        textKey: 'contacts.activity.emailRemoved',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.emailRemovedBy' }
          : undefined,
      };
    // Contact events
    case 'CONTACT_INBOUND_CALL': {
      const isVoicemail = callStatus === 'VOICEMAIL';
      const isSentToAI = [
        'TRANSFERED_ELEVEN_LABS',
        'TRANSFERRED_ELEVEN_LABS',
        'TRANSFERRED_BLAND',
      ].includes(callStatus ?? '');
      const isDeclined = callDetail === 'RINGING_REJECTED';

      if (isVoicemail) {
        return {
          icon: Icons.Voicemail,
          textKey: 'contacts.activity.sentToVoicemail',
          iconClassName: 'text-extra-decline',
          user: userDisplay
            ? {
                ...userDisplay,
                prefixKey: isDeclined
                  ? 'contacts.activity.declinedBy'
                  : 'contacts.activity.calledBy',
                suffixKey: isDeclined ? 'contacts.activity.sentToVoicemailSuffix' : undefined,
              }
            : undefined,
        };
      }

      if (isSentToAI) {
        return {
          icon: Icons.Ai,
          textKey: 'contacts.activity.sentToAI',
          iconClassName: 'text-highlight-default',
          user: userDisplay
            ? {
                ...userDisplay,
                prefixKey: isDeclined
                  ? 'contacts.activity.declinedBy'
                  : 'contacts.activity.calledBy',
                suffixKey: isDeclined ? 'contacts.activity.sentToAISuffix' : undefined,
              }
            : undefined,
        };
      }

      const wasPickedUp = isAnswered || (!!userName && !isDeclined);
      return {
        icon: wasPickedUp ? Icons.ArrowDownLeft : Icons.PhoneMissedCall,
        textKey: wasPickedUp
          ? 'contacts.activity.inboundCallAnswered'
          : 'contacts.activity.inboundCallMissed',
        iconClassName: wasPickedUp ? 'text-highlight-default' : 'text-extra-decline',
        user: userDisplay
          ? {
              ...userDisplay,
              prefixKey: wasPickedUp
                ? 'contacts.activity.answeredBy'
                : isDeclined
                  ? 'contacts.activity.declinedBy'
                  : 'contacts.activity.calledBy',
              suffixKey: wasPickedUp ? undefined : 'contacts.activity.noOneAnswered',
            }
          : undefined,
      };
    }
    case 'CONTACT_OUTBOUND_CALL': {
      const isOutboundVoicemail = callStatus === 'VOICEMAIL';

      if (isOutboundVoicemail) {
        return {
          icon: Icons.Voicemail,
          textKey: 'contacts.activity.sentToVoicemail',
          iconClassName: 'text-highlight-default',
          user: userDisplay
            ? { ...userDisplay, prefixKey: 'contacts.activity.calledBy' }
            : undefined,
        };
      }

      return {
        icon: Icons.ArrowUpRight,
        textKey: isAnswered
          ? 'contacts.activity.outboundCallAnswered'
          : 'contacts.activity.outboundCallNotAnswered',
        iconClassName: 'text-highlight-default',
        user: userDisplay ? { ...userDisplay, prefixKey: 'contacts.activity.calledBy' } : undefined,
      };
    }
    case 'CONTACT_INBOUND_SMS':
      return {
        icon: Icons.ChatBubble,
        textKey: 'contacts.activity.smsReceived',
        iconClassName: 'text-highlight-default',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.receivedBy' }
          : undefined,
      };
    case 'CONTACT_OUTBOUND_SMS':
      return {
        icon: Icons.ChatBubble,
        textKey: 'contacts.activity.smsSent',
        iconClassName: 'text-highlight-default',
        user: userDisplay ? { ...userDisplay, prefixKey: 'contacts.activity.sentBy' } : undefined,
      };
    // Company events (without CONTACT_ prefix)
    case 'INBOUND_CALL':
      return {
        icon: isAnswered ? Icons.ArrowDownLeft : Icons.PhoneMissedCall,
        textKey:
          userName && contactName
            ? isAnswered
              ? 'companies.activity.inboundCallAnsweredByWithContact'
              : 'companies.activity.inboundCallMissedWithContact'
            : userName
              ? isAnswered
                ? 'companies.activity.inboundCallAnsweredBy'
                : 'companies.activity.inboundCallMissed'
              : contactName
                ? isAnswered
                  ? 'companies.activity.inboundCallAnsweredWithContact'
                  : 'companies.activity.inboundCallMissedWithContact'
                : isAnswered
                  ? 'contacts.activity.inboundCallAnswered'
                  : 'contacts.activity.inboundCallMissed',
        iconClassName: isAnswered ? 'text-highlight-default' : 'text-extra-decline',
        interpolation:
          userName || contactName ? { actor: userName ?? '', contact: contactName } : undefined,
      };
    case 'OUTBOUND_CALL':
      return {
        icon: Icons.ArrowUpRight,
        textKey:
          userName && contactName
            ? 'companies.activity.outboundCallByWithContact'
            : userName
              ? 'companies.activity.outboundCallBy'
              : contactName
                ? 'companies.activity.outboundCallWithContact'
                : isAnswered
                  ? 'contacts.activity.outboundCallAnswered'
                  : 'contacts.activity.outboundCallNotAnswered',
        iconClassName: isAnswered ? 'text-highlight-default' : 'text-extra-decline',
        interpolation:
          userName || contactName ? { actor: userName ?? '', contact: contactName } : undefined,
      };
    case 'INBOUND_SMS':
      return {
        icon: Icons.ChatBubble,
        textKey:
          userName && contactName
            ? 'companies.activity.inboundSmsByWithContact'
            : userName
              ? 'companies.activity.inboundSmsBy'
              : contactName
                ? 'companies.activity.inboundSmsWithContact'
                : 'contacts.activity.smsReceived',
        iconClassName: 'text-highlight-default',
        interpolation:
          userName || contactName ? { actor: userName ?? '', contact: contactName } : undefined,
      };
    case 'OUTBOUND_SMS':
      return {
        icon: Icons.ChatBubble,
        textKey:
          userName && contactName
            ? 'companies.activity.outboundSmsByWithContact'
            : userName
              ? 'companies.activity.outboundSmsBy'
              : contactName
                ? 'companies.activity.outboundSmsWithContact'
                : 'contacts.activity.smsSent',
        iconClassName: 'text-highlight-default',
        interpolation:
          userName || contactName ? { actor: userName ?? '', contact: contactName } : undefined,
      };
    case 'TEAM_MEMBER_ADDED':
      return {
        icon: Icons.UserPlus,
        textKey:
          contactName && userName
            ? 'companies.activity.teamMemberAddedByWithContact'
            : contactName
              ? 'companies.activity.teamMemberAddedWithContact'
              : userName
                ? 'companies.activity.teamMemberAddedBy'
                : 'companies.activity.teamMemberAdded',
        interpolation:
          contactName || userName ? { contact: contactName, actor: userName ?? '' } : undefined,
      };
    case 'TEAM_MEMBER_REMOVED':
      return {
        icon: Icons.UserMinus,
        textKey:
          contactName && userName
            ? 'companies.activity.teamMemberRemovedByWithContact'
            : contactName
              ? 'companies.activity.teamMemberRemovedWithContact'
              : userName
                ? 'companies.activity.teamMemberRemovedBy'
                : 'companies.activity.teamMemberRemoved',
        interpolation:
          contactName || userName ? { contact: contactName, actor: userName ?? '' } : undefined,
      };
    case 'COMPANY_UPDATED':
      return {
        icon: Icons.Edit,
        textKey: userName
          ? 'companies.activity.companyUpdatedBy'
          : 'companies.activity.companyUpdated',
        interpolation: userName ? { name: userName } : undefined,
      };
    case 'COMPANY_UPDATED_FROM_INTEGRATION': {
      const integrationType = payload?.integration_type;
      // Try to match with lowercase version for API values that come in uppercase
      const integrationStyle = integrationType
        ? INTEGRATION_STYLES.find(
            (s) =>
              s.name === integrationType ||
              s.name === integrationType.toLowerCase() ||
              s.name === integrationType.toLowerCase().replace(/_/g, '-'),
          )
        : undefined;
      const integrationName = integrationType
        ? getIntegrationDisplayName(integrationType)
        : undefined;

      return {
        icon: Icons.Plus,
        // Use simple text key - integration name will be rendered separately with different styling
        textKey: 'companies.activity.companyUpdatedFrom',
        integration: integrationType
          ? {
              type: integrationType,
              name: integrationName ?? integrationType,
              logo: integrationStyle?.glass ?? integrationStyle?.image,
            }
          : undefined,
      };
    }
    case 'COMPANY_PHONE_NUMBER_ASSIGN':
      return {
        icon: Icons.Phone,
        textKey: userName
          ? 'companies.activity.phoneNumberAssignedBy'
          : 'companies.activity.phoneNumberAssigned',
        interpolation: userName ? { name: userName } : undefined,
      };
    case 'CALL_TAG_ADDED': {
      const isAiAdded = payload?.source === 'AI';
      return {
        icon: Icons.Tag,
        textKey: isAiAdded
          ? 'contacts.activity.callTagAddedByAi'
          : 'contacts.activity.callTagAdded',
        user:
          userDisplay && !isAiAdded
            ? { ...userDisplay, prefixKey: 'contacts.activity.addedBy' }
            : undefined,
        tag: payload?.tag_name
          ? { name: payload.tag_name, colors: getTagColors(payload.tag_hex_color) }
          : undefined,
      };
    }
    case 'CALL_TAG_REMOVED':
      return {
        icon: Icons.Tag,
        textKey: 'contacts.activity.callTagRemoved',
        user: userDisplay
          ? { ...userDisplay, prefixKey: 'contacts.activity.removedBy' }
          : undefined,
        tag: payload?.tag_name
          ? { name: payload.tag_name, colors: getTagColors(payload.tag_hex_color) }
          : undefined,
      };
    default:
      return { icon: Icons.History, textKey: 'contacts.activity.activity' };
  }
}

export type ActivityCellProps = {
  activity?: ActivityEvent | null;
  /** Hide the icon (useful when rendering in a custom timeline) */
  hideIcon?: boolean;
  className?: string;
};

export const ActivityCell = memo(function ActivityCell({
  activity,
  hideIcon,
  className,
}: ActivityCellProps) {
  const { t } = useTranslation('desktop_common');
  const activityDisplay = activity ? getActivityDisplay(activity) : null;
  const resolvedUserImageUrl = useResolvedMediaUrl(activityDisplay?.user?.imageUrl ?? undefined);

  if (!activity || !activityDisplay) {
    return null;
  }

  const { icon, textKey, iconClassName, interpolation, integration, user, tag } = activityDisplay;

  const tStr = t as (key: string, options?: Record<string, string>) => string;
  const translatedText = tStr(textKey, interpolation);

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5 text-primary-60', className)}>
      {!hideIcon && <Icon name={icon} className={cn('text-s shrink-0', iconClassName)} />}
      {user ? (
        <>
          <P3 className="shrink-0">{tStr(user.prefixKey)}</P3>
          <Chip size="sm" color="highlight">
            {resolvedUserImageUrl ? (
              <Avatar type="image" src={resolvedUserImageUrl} name={user.name} />
            ) : (
              <Avatar type="initial" name={user.name} />
            )}
          </Chip>
          <P3 className="shrink-0 text-primary-100">{user.name}</P3>
          {user.suffixKey && <P3 className="truncate">{tStr(user.suffixKey)}</P3>}
        </>
      ) : (
        <P3 className="truncate">{translatedText}</P3>
      )}
      {tag && (
        <Badge colors={tag.colors} className="scale-90 origin-left">
          {tag.name}
        </Badge>
      )}
      {integration && (
        <div className="flex shrink-0 items-center gap-0.5">
          <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-primary-5">
            {integration.logo && (
              <img
                src={integration.logo}
                alt={integration.name}
                className="size-2.5 object-contain"
              />
            )}
          </div>
          <P3 className="text-primary-80">{integration.name}</P3>
        </div>
      )}
    </div>
  );
});
